import { Injectable, Injector, inject } from '@angular/core';
import { Observable, concat, defer, endWith, ignoreElements, map, of } from 'rxjs';
import { AlertRecipientsHelper } from 'src/app/alert/shared/alert-builder';
import { RecipientSettings } from 'src/app/hickory-api';
import { DirectoryInjector } from '../base/directory-injector';
import { DirectoryGroup, DirectoryUser, PagedDirectoryGroups, PagedDirectoryUsers, PseudoDirectory } from '../base/models';
import { HandweaveDirectoryService } from '../handweave/handweave-directory.service';
import { HickoryDirectoryService } from '../hickory/hickory-directory.service';
import { BaseDirectoryService } from '../base/base-directory.service';
import { getPageSlice } from '../../model/utility';

@Injectable()
export class CombinedDirectoryService {
  private readonly hkService: HickoryDirectoryService;
  private readonly hwService: HandweaveDirectoryService;

  constructor() {
    const injector = inject(Injector);

    this.hkService = DirectoryInjector.getHickoryDirectory(injector, PseudoDirectory.psuedoId());
    this.hkService.clearClassification(); // don't filter requests by classification for this instance
    this.hwService = DirectoryInjector.getHandweaveDirectory(injector, undefined);
  }

  resolveRecipients(recipients: RecipientSettings): Observable<void> {
    // Resolve users first, then groups sequentially. defer() ensures resolveGroups()
    // is not called until resolveUsers() completes (lazy evaluation).
    return concat(
      this.resolveUsers(recipients, false, 1, 500),
      defer(() => this.resolveGroups(recipients, 1, 500))
    ).pipe(
      ignoreElements(),           // discard individual items — we only care about completion
      endWith(undefined as void)  // emit void once both observables complete
    );
  }

  resolveUsers(recipients: RecipientSettings, includeGroups: boolean, page?: number, pageSize?: number, sort?: string): Observable<PagedDirectoryUsers> {
    const hkUserIds: string[] = recipients?.contactIds?.map(id => '' + id) ?? [];
    const hkGroupIds: string[] = includeGroups ? recipients?.groupIds?.map(id => '' + id) ?? [] : null;
    let hwUserIds: string[] = [];
    let hwGroupIds: string[] = [];
    recipients?.directories?.forEach(dir => {
      hwUserIds = hwUserIds.concat(dir.contactIds ?? []);
      hwGroupIds = includeGroups ? hwGroupIds.concat(dir.groupIds ?? []) : null;
    });

    if (!!hkUserIds?.length || !!hkGroupIds?.length) {
      return this.hkService.getUsersById(hkUserIds, hkGroupIds, page, pageSize, sort).pipe(map(res => {
        // find missing users
        this.checkForMissingUsers(hkUserIds, res?.items, this.hkService, page, pageSize);
        return res;
      }));
    } else if (!!hwUserIds?.length || !!hwGroupIds?.length) {
      return this.hwService.getUsersById(hwUserIds, hwGroupIds, page, pageSize, sort).pipe(map(res => {
        // find missing users
        this.checkForMissingUsers(hwUserIds, res?.items, this.hwService, page, pageSize);
        return res;
      }));
    } else {
      const res: PagedDirectoryUsers = { items: [], page: page, pageSize: pageSize, totalItemCount: 0 };
      return of(res);
    }
  }

  private checkForMissingUsers(ids: string[], items: DirectoryUser[], service: BaseDirectoryService, page?: number, pageSize?: number) {
    // handle the case where we're resolving a large number of user IDs in batches
    if (ids?.length > pageSize) ids = getPageSlice(ids, page, pageSize);
    ids.forEach(id => {
      if (!items?.find(x => x.id === id)) {
        const missing = DirectoryUser.fromMissingId(id, service.directoryId, service);
        service.putUser(missing);
        items.push(missing);
      }
    });
  }

  private checkForMissingGroups(ids: string[], items: DirectoryGroup[], service: BaseDirectoryService, page?: number, pageSize?: number) {
    // handle the case where we're resolving a large number of user IDs in batches
    if (ids?.length > pageSize) ids = getPageSlice(ids, page, pageSize);
    ids.forEach(id => {
      if (!items?.find(x => x.id === id)) {
        const missing = DirectoryGroup.fromMissingId(id, service.directoryId, service);
        service.putGroup(missing);
        items.push(missing);
      }
    });
  }

  resolveGroups(recipients: RecipientSettings, page?: number, pageSize?: number): Observable<PagedDirectoryGroups> {
    const hkGroupIds: string[] = recipients?.groupIds?.map(id => '' + id) ?? [];
    const hwGroupIds: string[] = AlertRecipientsHelper.allRecipientsDirectoryGroupIds(recipients);
    if (hkGroupIds.length) {
      return this.hkService.getGroupsById(hkGroupIds, page, pageSize).pipe(map(res => {
        // find missing groups
        this.checkForMissingGroups(hkGroupIds, res?.items, this.hkService, page, pageSize);
        return res;
      }));
    } else if (hwGroupIds.length) {
      return this.hwService.getGroupsById(hwGroupIds, page, pageSize).pipe(map(res => {
        // find missing groups
        this.checkForMissingGroups(hwGroupIds, res?.items, this.hwService, page, pageSize);
        return res;
      }));
    } else {
      const res: PagedDirectoryGroups = { items: [], page: page, pageSize: pageSize, totalItemCount: 0 };
      return of(res);
    }
  }
}
